import axiosInstance from "../../common/axiosInstance";
import { useState } from "react";
import type { FireDrill } from "../../types/api";
import { useOrgUsersStore } from "../../store/store";
import { getS3SignedUrl } from "../../utils/s3Client";

interface SendFireDrillEmailProps {
  fireDrillData: FireDrill;
  recipientEmails: string[];
  onError: (error: string) => void;
}

interface EmailContent {
  subject: string;
  body: string;
  attachments: { fileName: string; fileUrl: string; fileType: string }[];
}

const SendFireDrillEmail = ({
  fireDrillData,
  recipientEmails,
  onError,
}: SendFireDrillEmailProps) => {
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>("");
  const { orgUsersData } = useOrgUsersStore();

  const getResponsiblePersonDetails = () => {
    if (!fireDrillData.person_responsibility || !orgUsersData) {
      return { name: "Not assigned", email: "" };
    }

    const responsibleUser = orgUsersData.find((user: any) => {
      const userId =
        typeof user._id === "object" && "$oid" in user._id
          ? user._id
          : String(user._id);

      return userId === fireDrillData.person_responsibility;
    });
    return {
      name: responsibleUser
        ? `${responsibleUser.firstName} ${responsibleUser.lastName}`
        : "Not available",
      email: responsibleUser?.email || "",
    };
  };

  const getFileType = (fileName: string): string => {
    if (!fileName) return "File";
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    switch (extension) {
      case "pdf":
        return "PDF Document";
      case "doc":
      case "docx":
        return "Word Document";
      case "xls":
      case "xlsx":
        return "Excel Document";
      case "ppt":
      case "pptx":
        return "PowerPoint";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "Image";
      case "txt":
        return "Text File";
      default:
        return "File";
    }
  };

  const getMimeType = (extension: string): string => {
    switch (extension.toLowerCase()) {
      case "pdf":
        return "application/pdf";
      case "doc":
      case "docx":
        return "application/msword";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "txt":
        return "text/plain";
      default:
        return "application/octet-stream";
    }
  };

  const generateEmailContent = async (data: FireDrill): Promise<EmailContent> => {
    const { name: responsibleName } = getResponsiblePersonDetails();

    // Determine module type for email content
    const moduleType = data.moduleType || "fire_safety";
    const isHazardousMaterials = moduleType === "hazardous_materials";
    const isExternalEmergencyPreparedness =
      moduleType === "external_emergency_preparedness";

    const title = isHazardousMaterials
      ? "Hazardous Materials Drill Report"
      : isExternalEmergencyPreparedness
      ? "External Emergency Preparedness Drill Report"
      : "Fire Drill Report";

    const teamName = isHazardousMaterials
      ? "Hazardous Materials Management Team"
      : isExternalEmergencyPreparedness
      ? "External Emergency Preparedness Management Team"
      : "Fire Drill Management Team";

    const fileAttachments = data.evidence_closure_files?.length
      ? await Promise.all(
          data.evidence_closure_files.map(async (file) => {
            let fileUrl: string;
            let fileName: string;

            if (typeof file === "string") {
              fileName = file.split("/").pop() || "Unknown File";
              const extension = fileName.split(".").pop() || "";
              try {
                fileUrl = await getS3SignedUrl(file, getMimeType(extension));
              } catch {
                fileUrl = "Unavailable";
              }
            } else if (file && typeof file === "object") {
              if ("url" in file && typeof file.url === "string") {
                fileUrl = file.url;
                fileName = fileUrl.split("/").pop() || "Unknown File";
              } else if ("name" in file && typeof file.name === "string") {
                fileName = file.name;
                fileUrl =
                  "webkitRelativePath" in file || "size" in file
                    ? URL.createObjectURL(file)
                    : fileName;
              } else {
                fileName = "Unknown File";
                fileUrl = "Unknown File";
              }
            } else {
              fileName = "Unknown File";
              fileUrl = "Unknown File";
            }

            const fileType = getFileType(fileName);
            return { fileName, fileUrl, fileType };
          })
        )
      : [];
    const fileLinks = fileAttachments.length
      ? fileAttachments
          .map((file) => `${file.fileName} (${file.fileUrl})`)
          .join("\n ")
      : "No files attached";

    return {
      subject: `${title} - ${data.premise} - ${data.date_of_test_drills}`,
      body: `
      ${title} - Date - ${data.date_of_test_drills}

      ======================
      DRILL INFORMATION
      ======================
      Region: ${data.region}
      Premise: ${data.premise}
      Date: ${data.date_of_test_drills}
      Responsible Person: ${responsibleName}
      ${
        isHazardousMaterials
          ? "Drill Type: Hazardous Materials Safety Drill"
          : isExternalEmergencyPreparedness
          ? "Drill Type: External Emergency Preparedness Drill"
          : "Drill Type: Fire Safety Drill"
      }

      ======================
      PERFORMANCE METRICS
      ======================
      Participation Rate: ${data.participation_rate || "N/A"}%
      Average Drill Duration: ${data.average_drill} minutes
      ${
        isHazardousMaterials ? "Response Time Trend" : "Evacuation Time Trend"
      }: ${data.evacuation_trend} minutes

      ======================
      OBSERVATIONS & ACTIONS
      ======================
      Observations for Improvement:
      ${data.observation_identified_for_improvement || "N/A"}
      
      Actions Taken:
      ${data.actions || "N/A"}

      ${
        isExternalEmergencyPreparedness
          ? `
      Feedback & Gap Analysis:
      ${data.feedback_gap_analysis || "N/A"}
      `
          : ""
      }

      ======================
      CLOSURE DETAILS
      ======================
      Closure Evidence:
      ${data.closure_evidence || "N/A"}
      
      Next Scheduled Drill:
      ${data.next_drill_scheduled || "Not scheduled yet"}

      ======================
      ATTACHMENTS
      ======================
      ${fileLinks}
      
      Thank you,
      ${teamName}
    `.trim(),
      attachments: fileAttachments,
    };
  };

  const sendEmail = async (): Promise<boolean> => {
    try {
      const { subject, body } = await generateEmailContent(fireDrillData);
      const { email: responsibleEmail } = getResponsiblePersonDetails();

      // Combine all recipients (original recipients + responsible person)
      const allRecipients = Array.from(
        new Set([...(recipientEmails || []), responsibleEmail].filter(Boolean))
      );

      if (allRecipients.length > 0) {
        const response = await axiosInstance.post(`/api/email`, {
            to: allRecipients,
            cc: [],
            bcc: [],
            subject,
            body,
            templateType: "generic",
            additionalVariables: [],
          });

        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to send email");
        }
      }

      setEmailErrorMessage("");
      return true;
    } catch (error) {
      const errorMsg = `Email sending failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`;
      setEmailErrorMessage(errorMsg);
      onError(errorMsg);
      return false;
    }
  };

  return {
    sendEmail,
    emailErrorMessage,
  };
};

export default SendFireDrillEmail;
